"use client";

import { useEffect, useState } from "react";
import { LuLoader2 } from "react-icons/lu";

import { dedupe } from "~/utils/various";
import { useEventContextV2, useWhiteLabel } from "~/app/providers";
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from "~/components/v4/shadcn/Tabs";
import Modal from "~/components/v3/themed/Modal";
import { Button } from "~/components/v4/themed/Button";
import { UserAvatar } from "~/components/v4/shadcn/UserAvatar";
import { TextArea } from "~/components/v3/themed/TextArea";
import { Input } from "~/components/v4/shadcn/Input";
import { toastHandler } from "~/components/ToastHandler";

import { InviteGuests } from "./invite_components/InviteGuests";
import { PreapproveGuests } from "./invite_components/PreapproveGuests";
import { InviteFollowers } from "./invite_components/InviteFollowers";
import { NearbyGuests } from "./invite_components/NearbyGuests";
import { useInvites } from "./contexts/InvitesContext";
import {
    GetProximityAndFollowersInvites,
    sendEventInvites as InvitePreviousGuests,
} from "~/app/events/[slug]/invites/actions";
import { bulkPreApprove, sendInvites, type Invite } from "../actions";
import { cn } from "~/utils/fontsConfig";
import { If } from "~/components/v3/Conditional";
import { useSession } from "next-auth/react";

export type Tabs = "preapprove" | "invite" | "proximity" | "followers";
export const EventInvitesModal = ({
    isOpen,
    onClose,
    eventId,
    refetch,
    initialData,
}: {
    isOpen: boolean;
    onClose: () => void;
    eventId: string;
    refetch?: () => void;
    initialData: GetProximityAndFollowersInvites;
}) => {
    const event = useEventContextV2();
    const isCommunityEvent = event.community !== null;
    const customization = useWhiteLabel()?.customization;

    const [loading, setLoading] = useState(false);
    const [step, setStep] = useState<"select" | "form">("select");
    const [selectedTab, setSelectedTab] = useState<Tabs>(
        isCommunityEvent ? "proximity" : "followers",
    );

    const {
        invites,
        preapproved,
        proximity,
        selectedFollowers,
        message,
        subject,
        from,
        clearInvitesState,
        setTotalCount,
    } = useInvites();

    useEffect(() => {
        if (initialData) {
            setTotalCount((prev) => ({
                ...prev,
                followers: initialData.followers.length,
                proximity: initialData.eligibleUsers.length,
            }));
        }
    }, [initialData]);

    const handleNext = () => {
        setStep("form");
    };

    const handleBack = () => {
        setStep("select");
    };

    const handleSendInvites = () => {
        const nearByAndFollowers = dedupe(
            [...proximity, ...selectedFollowers],
            "id",
        );

        setLoading(true);
        toastHandler({
            loadingMessage: "Sending invites...",
            successMessage: "Invites sent successfully",
            operation: async () => {
                if (preapproved.length > 0) {
                    await bulkPreApprove(eventId, preapproved, message);
                }
                if (invites.length > 0) {
                    await sendInvites(eventId, invites, message, subject, from);
                }
                if (nearByAndFollowers.length > 0) {
                    await InvitePreviousGuests({
                        params: {
                            eventSlug: event.slug,
                            event,
                            selectedUserIds: nearByAndFollowers.map(
                                (invite) => invite.id,
                            ),
                            message,
                            subject,
                            from,
                        },
                    });
                }
            },
            onSuccess: () => {
                refetch?.();
            },
            onSettled: () => {
                setStep("select");
                setSelectedTab("invite");
                clearInvitesState();
                setLoading(false);
                onClose();
            },
        });
    };

    // This function check whether user is inviting from multiple tabs or a single tab at the same time.
    const selectedMode = () => {
        const counts = {
            preapprove: preapproved.length,
            invite: invites.length,
            proximity: proximity.length,
            followers: selectedFollowers.length,
        };

        const isOnlyOneTypeSelected =
            Object.entries(counts).filter(([_, count]) => count > 0).length ===
            1;

        if (!isOnlyOneTypeSelected) return "multiple";

        // Find the first type that has a count > 0
        const selectedType = Object.entries(counts).find(
            ([_, count]) => count > 0,
        )?.[0];

        // TypeScript type assertion to ensure it's a valid Tab type since this is how we get the label in our record below
        return (selectedType as Tabs) || "multiple";
    };

    const getButtonContent = ({
        loading,
        step,
    }: {
        loading: boolean;
        step: "select" | "form";
    }) => {
        if (loading) {
            return (
                <span className="flex items-center gap-2">
                    <LuLoader2 className="h-4 w-4 animate-spin" />
                    <span>Sending...</span>
                </span>
            );
        }

        if (step === "select") return "Next";

        const buttonLabels = {
            preapprove: `Send Pre-Approval(${preapproved.length})`,
            invite: `Send Email Invite(${invites.length})`,
            proximity: `Invite Nearby Guest(${proximity.length})`,
            followers: `Invite Follower(${selectedFollowers.length})`,
            multiple: `Send Invite(${totalInvites})`,
        } as const;

        return buttonLabels[selectedMode()];
    };

    const isDisabled =
        (step === "select" &&
            preapproved.length === 0 &&
            invites.length === 0 &&
            proximity.length === 0 &&
            selectedFollowers.length === 0) ||
        loading;

    const totalInvites =
        preapproved.length +
        invites.length +
        proximity.length +
        selectedFollowers.length;

    return (
        <Modal
            title="Invite Guests"
            open={isOpen}
            onOpenChange={() => {
                setStep("select");
                setSelectedTab("invite");
                clearInvitesState();
                onClose();
            }}
            className="flex max-h-[90vh] !max-w-[900px] flex-col !overflow-hidden pb-0"
        >
            <div className="mt-4 flex-1 md:mt-8">
                {step === "select" ? (
                    <InviteTabs
                        selectedTab={selectedTab}
                        setSelectedTab={setSelectedTab}
                        isCommunityEvent={isCommunityEvent}
                        initialData={initialData}
                    />
                ) : (
                    <InviteForm isCommunityEvent={isCommunityEvent} />
                )}
            </div>

            <div
                className={cn(
                    "sticky bottom-0 w-full rounded-b-lg border-t border-v3-N-400/50 bg-v3-N-500 p-2",
                    "md:bg-v3-N-600 md:px-0 md:py-4",
                    customization?.mainCardClassName,
                )}
            >
                <div className="flex w-full items-center justify-between">
                    {step === "select" ? (
                        <>
                            <span
                                className={cn(
                                    "text-sm text-gray-500",
                                    customization?.labelClassName,
                                )}
                            >
                                {totalInvites} guests
                            </span>
                        </>
                    ) : (
                        <Button
                            variant="secondary"
                            onClick={handleBack}
                            className={cn(customization?.buttons?.secondary)}
                        >
                            Back
                        </Button>
                    )}

                    <Button
                        onClick={
                            step === "select" ? handleNext : handleSendInvites
                        }
                        disabled={isDisabled}
                        className={cn(customization?.buttons?.default)}
                    >
                        {getButtonContent({ loading, step })}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

const InviteTabs = ({
    selectedTab,
    setSelectedTab,
    isCommunityEvent,
    initialData,
}: {
    selectedTab: Tabs;
    setSelectedTab: React.Dispatch<React.SetStateAction<Tabs>>;
    isCommunityEvent: boolean;
    initialData: GetProximityAndFollowersInvites;
}) => {
    return (
        <Tabs
            defaultValue={selectedTab}
            className="w-full"
            onValueChange={(value) => {
                setSelectedTab(value as Tabs);
            }}
        >
            <div className="flex min-h-[600px] flex-col gap-4 md:grid md:grid-cols-[260px_1fr] md:gap-6">
                {/* hidden in mobile */}
                <DesktopTabs
                    selectedTab={selectedTab}
                    isCommunityEvent={isCommunityEvent}
                />
                {/* hidden in desktop */}
                <MobileTabs isCommunityEvent={isCommunityEvent} />
                <div className="flex-1 pt-5 md:pt-0">
                    <TabsContent value="preapprove" className="mt-0">
                        <PreapproveGuests />
                    </TabsContent>
                    <TabsContent
                        value="invite"
                        className="small-scrollbar mt-0 max-h-[600px] overflow-auto pr-2"
                    >
                        <InviteGuests
                            invitedAttendees={
                                initialData.invitedAttendees ?? []
                            }
                        />
                    </TabsContent>
                    <TabsContent value="proximity" className="mt-0">
                        <NearbyGuests initialData={initialData} />
                    </TabsContent>
                    <TabsContent value="followers" className="mt-0">
                        <InviteFollowers initialData={initialData} />
                    </TabsContent>
                </div>
            </div>
        </Tabs>
    );
};

const MobileTabs = ({
    isCommunityEvent = true,
}: {
    isCommunityEvent: boolean;
}) => {
    return (
        <div className="w-full border-v3-N-400/50 md:hidden">
            <TabsList className="flex h-auto w-full flex-row overflow-x-scroll scrollbar-hide">
                <If condition={isCommunityEvent}>
                    <TabsTrigger
                        value="proximity"
                        className="w-full justify-start py-0 text-left md:px-2 md:py-2"
                    >
                        <span className="text-center text-sm font-medium">
                            Nearby Members
                        </span>
                    </TabsTrigger>
                </If>
                <TabsTrigger
                    value="followers"
                    className="w-full justify-start py-0 text-left md:px-2 md:py-2"
                >
                    <span className="text-center text-sm font-medium">
                        Followers
                    </span>
                </TabsTrigger>

                <TabsTrigger
                    value="invite"
                    className="w-full justify-start py-0 text-left md:px-2 md:py-2"
                >
                    <span className="text-center text-sm font-medium">
                        Invite Guests
                    </span>
                </TabsTrigger>

                <TabsTrigger
                    value="preapprove"
                    className="w-full justify-start py-0 text-left md:px-2 md:py-2"
                >
                    <span className="text-center text-sm font-medium">
                        Preapprove Guests
                    </span>
                </TabsTrigger>
            </TabsList>
        </div>
    );
};

const DesktopTabs = ({
    selectedTab,
    isCommunityEvent,
}: {
    selectedTab: Tabs;
    isCommunityEvent: boolean;
}) => {
    const { totalCount } = useInvites();

    return (
        <div className="hidden w-full md:block">
            <TabsList className="h-auto flex-col gap-6 space-x-0 rounded-[6px] border-none bg-v3-N-500 px-4 py-3 scrollbar-hide">
                <If condition={isCommunityEvent}>
                    <TabsTrigger
                        value="proximity"
                        className="w-full border-none p-0 text-left"
                    >
                        <span
                            className={cn(
                                "flex items-center gap-2 text-sm font-medium text-white",
                                selectedTab === "proximity" &&
                                    "text-v3-primary",
                            )}
                        >
                            <span>Invite nearby members</span>
                            <span className="text-sm text-gray-500">
                                {totalCount.proximity > 0 &&
                                    `(${totalCount.proximity})`}
                            </span>
                        </span>
                    </TabsTrigger>
                </If>
                <TabsTrigger
                    value="followers"
                    className="w-full border-none p-0 text-left"
                >
                    <span
                        className={cn(
                            "flex items-center gap-2 text-sm font-medium text-white",
                            selectedTab === "followers" && "text-v3-primary",
                        )}
                    >
                        <span>Invite followers</span>
                        <span className="text-sm text-gray-500">
                            {totalCount.followers > 0 &&
                                `(${totalCount.followers})`}
                        </span>
                    </span>
                </TabsTrigger>
                <TabsTrigger
                    value="invite"
                    className="w-full border-none p-0 text-left"
                >
                    <span
                        className={cn(
                            "text-sm font-medium text-white",
                            selectedTab === "invite" && "text-v3-primary",
                        )}
                    >
                        Invite contacts via email
                    </span>
                </TabsTrigger>
                <TabsTrigger
                    value="preapprove"
                    className="w-full border-none p-0 text-left"
                >
                    <span
                        className={cn(
                            "text-sm font-medium text-white",
                            selectedTab === "preapprove" && "text-v3-primary",
                        )}
                    >
                        Pre-approved guests
                    </span>
                </TabsTrigger>
            </TabsList>
        </div>
    );
};

const InviteForm = ({ isCommunityEvent }: { isCommunityEvent: boolean }) => {
    const event = useEventContextV2();
    const session = useSession();
    const {
        message,
        invites,
        preapproved,
        proximity,
        selectedFollowers,
        setMessage,
        setSubject,
        subject,
        from,
        setFrom,
    } = useInvites();

    const totalInvites =
        preapproved.length +
        invites.length +
        proximity.length +
        selectedFollowers.length;

    const nearByAndFollowers = dedupe(
        [...proximity, ...selectedFollowers],
        "id",
    );

    return (
        <div className="grid min-h-[600px] grid-cols-1 gap-6 md:grid-cols-[320px_1fr]">
            <div className="w-full">
                <div className="flex flex-col gap-4">
                    <span>Inviting {totalInvites} Guest(s)</span>

                    <div className="flex max-h-[550px] flex-col gap-1 overflow-y-auto">
                        {preapproved.length > 0 && (
                            <>
                                <div className="flex items-center gap-2 border-b-[1px] border-v3-N-400/50 p-3">
                                    <span className="text-sm text-white">
                                        Pre-approved guests
                                    </span>
                                </div>

                                {preapproved.map((invite, index) => (
                                    <InviteItem
                                        key={invite.email + invite.name + index}
                                        invite={invite}
                                    />
                                ))}
                            </>
                        )}

                        {invites.length > 0 && (
                            <>
                                <div className="flex items-center gap-2 border-b-[1px] border-v3-N-400/50 p-3">
                                    <span className="text-sm text-white">
                                        Invite contacts via email
                                    </span>
                                </div>

                                {invites.map((invite, index) => (
                                    <InviteItem
                                        key={invite.email + invite.name + index}
                                        invite={invite}
                                    />
                                ))}
                            </>
                        )}

                        {nearByAndFollowers.length > 0 && (
                            <>
                                <div className="flex items-center gap-2 border-b-[1px] border-v3-N-400/50 p-3">
                                    <span className="text-sm text-white">
                                        {isCommunityEvent
                                            ? "Proximity (nearby) and Followers"
                                            : "Followers"}
                                    </span>
                                </div>

                                {nearByAndFollowers.map((invite, index) => (
                                    <InviteItem
                                        key={invite.email + invite.name + index}
                                        invite={invite}
                                    />
                                ))}
                            </>
                        )}
                    </div>
                </div>
            </div>

            <div className="flex flex-col gap-4">
                {(invites.length > 0 || nearByAndFollowers.length) && (
                    <>
                        <div>
                            <span>Customize From (optional)</span>
                            <p className="mt-1 text-sm text-gray-500">
                                Customize from field of your invitation email
                            </p>
                        </div>

                        <Input
                            className="w-full rounded-[0.625rem] placeholder:font-[300] placeholder:not-italic placeholder:text-v3-N300"
                            placeholder="Events on River"
                            value={from}
                            onChange={(e) => setFrom(e.target.value)}
                        />

                        <div>
                            <span>Customize Subject (optional)</span>
                            <p className="mt-1 text-sm text-gray-500">
                                Customize subject of your invitation
                            </p>
                        </div>

                        <Input
                            className="w-full rounded-[0.625rem] placeholder:font-[300] placeholder:not-italic placeholder:text-v3-N300"
                            placeholder={`${session.data?.user?.name} invited you to ${event.name}`}
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                        />
                    </>
                )}
                <div>
                    <span>Customize Message (optional)</span>
                    <p className="mt-1 text-sm text-gray-500">
                        Add a personal message to your invitation
                    </p>
                </div>
                <TextArea
                    className="h-32 w-full rounded-[0.625rem] placeholder:font-[300] placeholder:not-italic placeholder:text-v3-N300"
                    placeholder="Hey! I'm hosting an event and would love for you to join..."
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                />
                <div className="text-sm text-gray-500">
                    We will send the event link to the provided emails along
                    with your customized message
                </div>
            </div>
        </div>
    );
};

const InviteItem = ({ invite }: { invite: Invite }) => {
    return (
        <div
            key={invite.email + invite.name}
            className="flex items-center gap-2 rounded-lg p-3"
        >
            <UserAvatar user={invite} />
            <div className="flex flex-col">
                <div className="text-sm text-white">{invite.name}</div>
                <div className="text-sm text-gray-500">{invite.email}</div>
            </div>
        </div>
    );
};
